import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { Button, Input, Table } from "reactstrap"
import SAlert from "components/Alert"

const ProvinceConsumptionItems = ({
  data,
  form,
  setForm,
  onSubmitQuantity,
  onUpdateFunc,
  setQuantity,
  quantity,
  onBatchSave,
  filters,
  storeStatus,
  onPendingEntriesChange,
}) => {
  const [editField, setEditField] = useState(null)
  const [pendingEntries, setPendingEntries] = useState([])

  // Notify parent component when pending entries change
  useEffect(() => {
    if (onPendingEntriesChange) {
      onPendingEntriesChange(pendingEntries.length > 0, pendingEntries.length)
    }
  }, [pendingEntries, onPendingEntriesChange])

  // Get user permissions from Redux store
  const userPermissions = useSelector(state => state.auth?.permissions || [])
  const canEditPreviousConsumption = userPermissions.includes(
    "edit-previous-consumption"
  )

  // set value to table when we clicked edit on next to record
  const onEditConsumption = consum => {
    setEditField({
      item_id: consum?.item_id,
      quantity: consum?.currentConsume?.consume,
      comment: consum?.currentConsume?.comment,
      id: consum?.currentConsume?.id,
    })
    setQuantity([
      {
        item_id: consum?.item_id,
        quantity: 0,
        comment: consum?.currentConsume?.comment,
      },
    ])
  }

  // call onUpdateFunc when we click update button
  const onUpdateConsumption = (value, consum) => {
    if (value) {
      value.province_id = consum?.province_id
      value.facility_id = consum?.facility_id
      value.item_category_id = consum?.item_category_id
      value.id = consum?.currentConsume?.id
      value.consume = value?.quantity
        ? value?.quantity
        : consum?.currentConsume?.consume
      value.comment = value?.comment
        ? value?.comment
        : consum?.currentConsume?.comment
      value.year = consum?.currentConsume?.year
      value.month = consum?.currentConsume?.month
      setQuantity([])
      setEditField(null)
      onUpdateFunc(value)
    }
  }

  // update qty when we add numbers
  const onQuantityChange = (e, item_id) => {
    if (e.target.value < 0) {
      return
    }
    setQuantity(prev =>
      prev.some(p => p.item_id === item_id)
        ? prev.map(qty =>
            qty.item_id === item_id ? { ...qty, quantity: e.target.value } : qty
          )
        : [...prev, { item_id: item_id, quantity: e.target.value }]
    )
  }

  // update comment when we add new comment
  const onCommnetChange = (e, item_id) => {
    if (e.target.value < 0) {
      return
    }
    setQuantity(prev =>
      prev.some(p => p.item_id === item_id)
        ? prev.map(qty =>
            qty.item_id === item_id ? { ...qty, comment: e.target.value } : qty
          )
        : [...prev, { item_id: item_id, comment: e.target.value }]
    )
  }

  // return total qty means (main qty - inserted qty)
  const showTotal = (qty, item_id) => {
    const check = quantity.find(qty => qty.item_id === item_id)

    if (check) {
      if (editField?.item_id === item_id) {
        return qty + editField?.quantity - check.quantity
      } else {
        return qty - check.quantity
      }
    } else {
      return qty
    }
  }

  // Add entry to pending list instead of immediate save
  const addToPendingList = async (value, consum) => {
    // Check if this item already exists in pending entries
    const isDuplicate = pendingEntries.some(
      entry =>
        entry.item_id === consum?.item_id &&
        entry.facility_id === consum?.facility_id &&
        entry.province_id === consum?.province_id &&
        entry.item_category_id === consum?.item_category_id &&
        entry.project_id === consum?.project_id
    )

    if (isDuplicate) {
      await SAlert({
        title: "Duplicate Entry!",
        text: `This item for ${consum?.facility?.facility_name} is already in your pending list. Please save or remove it first.`,
        icon: "warning",
      })
      return // Prevent adding duplicate
    }

    const year = filters?.year ? filters.year : new Date().getFullYear()
    const month = filters?.month
      ? filters.month
      : ("0" + (new Date().getMonth() + 1)).slice(-2)

    const newEntry = {
      id: Date.now() + Math.random(), // Temporary unique ID for local state
      item_id: consum?.item_id,
      facility_id: consum?.facility_id,
      province_id: consum?.province_id,
      item_category_id: consum?.item_category_id,
      provided_by: consum?.provided_by,
      project_id: consum?.project_id,
      quantity: value.quantity,
      comment: value.comment || "",
      year: year,
      month: month,
      // Display data
      item: consum?.item,
      province: consum?.province,
      facility: consum?.facility,
    }

    setPendingEntries(prev => [...prev, newEntry])
    setQuantity([])
    setEditField(null)
  }

  // Remove entry from pending list
  const removeFromPendingList = id => {
    setPendingEntries(prev => prev.filter(entry => entry.id !== id))
  }

  // Check if item is already in pending list
  const isInPendingList = consum => {
    return pendingEntries.some(
      entry =>
        entry.item_id === consum?.item_id &&
        entry.facility_id === consum?.facility_id &&
        entry.province_id === consum?.province_id &&
        entry.item_category_id === consum?.item_category_id &&
        entry.project_id === consum?.project_id
    )
  }

  // Clear all pending entries
  const clearPendingEntries = () => {
    setPendingEntries([])
  }

  // Save all pending entries
  const handleSaveAll = () => {
    if (pendingEntries.length === 0) return

    // Prepare data for batch save (remove display-only fields)
    const consumptionsToSave = pendingEntries.map(entry => ({
      item_id: entry.item_id,
      facility_id: entry.facility_id,
      province_id: entry.province_id,
      item_category_id: entry.item_category_id,
      provided_by: entry.provided_by,
      project_id: entry.project_id,
      quantity: entry.quantity,
      comment: entry.comment,
      year: entry.year,
      month: entry.month,
    }))

    onBatchSave(consumptionsToSave)
    clearPendingEntries()
  }

  // check status of storing for add button
  const checkFormAdd = (qty, consum) => {
    const value = quantity.find(qty => qty.item_id === consum?.item_id)
    const itemInPending = isInPendingList(consum)

    // Show message if item is already in pending list
    if (itemInPending) {
      return (
        <span className="badge badge-soft-success">
          <i className="bx bx-check me-1"></i>
          Already in Pending
        </span>
      )
    }

    if (value && value?.quantity !== "") {
      const total = showTotal(qty, consum?.item_id)
      if (total >= 0 && total <= qty && total !== NaN) {
        return (
          <Button
            onClick={e => addToPendingList(value, consum)}
            className="btn btn-sm btn-info"
            type="button"
            disabled={storeStatus?.loading}
          >
            Add to List
          </Button>
        )
      } else {
        return <span className="text-danger">Out of Quantity</span>
      }
    } else {
      return
    }
  }

  // check status of update for add button
  const checkFormUpdate = (qty, consum) => {
    const value = quantity.find(qty => qty.item_id === consum?.item_id)
    if (value && value?.quantity !== "") {
      const total = showTotal(qty, consum?.item_id)
      if (total >= 0 && total <= qty + editField?.quantity && total !== NaN) {
        return (
          <Button
            onClick={e => onUpdateConsumption(value, consum)}
            className="btn btn-sm btn-success"
            type="button"
            disabled={storeStatus?.loading}
          >
            Update
          </Button>
        )
      } else {
        return <span className="text-danger">Out of Quantity</span>
      }
    } else {
      return
    }
  }

  return (
    <>
      <div className="table-responsive">
        <Table>
          <thead>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>UNFPA ITEM ID</th>
              <th style={{ whiteSpace: "nowrap" }}>IP- ITEM DESCRIPTION</th>
              <th style={{ whiteSpace: "nowrap" }}>UNFPA UOM</th>
              <th style={{ whiteSpace: "nowrap" }}>IP UOM</th>
              <th style={{ whiteSpace: "nowrap" }}>PROVINCE</th>
              <th style={{ whiteSpace: "nowrap" }}>FACILITY</th>
              <th style={{ whiteSpace: "nowrap" }}>OPENING BALANCE</th>
              <th style={{ whiteSpace: "nowrap" }}>CONSUMPTION</th>
              <th style={{ whiteSpace: "nowrap" }}>
                COMMENT &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </th>
              <th style={{ whiteSpace: "nowrap" }}>LAST BALANCE</th>
              <th style={{ whiteSpace: "nowrap" }}></th>
            </tr>
          </thead>
          <tbody>
            {data &&
              data?.map((consum, key) => {
                const itemInPending = isInPendingList(consum)

                const consumption = consum?.currentConsume ? (
                  editField?.item_id === consum?.item_id ? (
                    <Input
                      name="update_consume"
                      placeholder="Qty"
                      defaultValue={editField?.quantity}
                      onChange={e => onQuantityChange(e, consum.item_id)}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault()
                          const value = quantity.find(
                            qty => qty.item_id === consum?.item_id
                          )
                          if (value && value?.quantity !== "") {
                            const total = showTotal(
                              lastBalance,
                              consum?.item_id
                            )
                            if (
                              total >= 0 &&
                              total <= lastBalance + editField?.quantity &&
                              total !== NaN
                            ) {
                              onUpdateConsumption(value, consum)
                            }
                          }
                        }
                      }}
                      disabled={itemInPending}
                    />
                  ) : (
                    consum?.currentConsume?.consume
                  )
                ) : (
                  <Input
                    name="consume"
                    placeholder="Qty"
                    onChange={e => onQuantityChange(e, consum.item_id)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        const value = quantity.find(
                          qty => qty.item_id === consum?.item_id
                        )
                        if (value && value?.quantity !== "" && !itemInPending) {
                          const total = showTotal(lastBalance, consum?.item_id)
                          if (
                            total >= 0 &&
                            total <= lastBalance &&
                            total !== NaN
                          ) {
                            addToPendingList(value, consum)
                          }
                        }
                      }
                    }}
                    disabled={itemInPending}
                  />
                )

                const consumptionComment = consum?.currentConsume ? (
                  editField?.item_id === consum?.item_id ? (
                    <Input
                      name="update_comment"
                      placeholder="comment"
                      defaultValue={editField?.comment}
                      onChange={e => onCommnetChange(e, consum.item_id)}
                      disabled={itemInPending}
                    />
                  ) : (
                    consum?.currentConsume?.comment
                  )
                ) : (
                  <Input
                    name="comment"
                    placeholder="comment"
                    onChange={e => onCommnetChange(e, consum.item_id)}
                    disabled={itemInPending}
                  />
                )

                const opeining_balance = consum?.totalQty - consum?.consumes

                const currConsume = consum?.currentConsume
                  ? consum?.currentConsume?.consume
                  : 0

                const lastBalance =
                  consum?.totalQty - (currConsume + consum?.consumes)

                return (
                  <tr
                    key={key}
                    className={itemInPending ? "table-success" : ""}
                  >
                    <td style={{ whiteSpace: "nowrap" }}>
                      {consum?.item.unfp_item_name}
                      {itemInPending && (
                        <span
                          className="badge badge-soft-success ms-2"
                          style={{ fontSize: "10px" }}
                        >
                          In Pending List
                        </span>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {consum?.item.ip_item_description}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {consum?.item.uom}{" "}
                      {consum?.item.unfpa_uom_quantity > 1 &&
                        consum?.item.unfpa_uom_quantity}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {consum?.item.ip_unit_umo}{" "}
                      {consum?.item.ip_uom_quantity > 1 &&
                        consum?.item.ip_uom_quantity}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {consum?.province?.province}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {consum?.facility?.facility_name} {consum?.facility_id}
                    </td>
                    <td>{opeining_balance}</td>
                    <td>{consumption}</td>
                    <td>{consumptionComment}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {showTotal(lastBalance, consum?.item_id)}
                    </td>
                    <td>
                      {/* Determine if user can edit this consumption record */}
                      {(() => {
                        const hasCurrentConsume = consum?.currentConsume
                        const isNotEditing =
                          consum?.item_id !== editField?.item_id
                        const hasNextMonthRecord = consum?.afterCurrentConsume

                        // Determine if Edit button should be shown
                        let showEditButton = false
                        let buttonTitle = ""

                        if (hasCurrentConsume && isNotEditing) {
                          if (canEditPreviousConsumption) {
                            // User has permission - can edit all records
                            showEditButton = true
                            buttonTitle = "Edit (You can edit previous records)"
                          } else {
                            // User doesn't have permission - only edit current month
                            if (!hasNextMonthRecord) {
                              showEditButton = true
                              buttonTitle = "Edit (Current month only)"
                            }
                          }
                        }

                        return (
                          <>
                            {showEditButton && (
                              <Button
                                onClick={() => onEditConsumption(consum)}
                                className="btn btn-sm btn-info"
                                type="button"
                                disabled={storeStatus?.loading}
                                title={buttonTitle}
                              >
                                <i className="mdi mdi-pencil me-1"></i>
                              </Button>
                            )}
                            {consum?.item_id === editField?.item_id
                              ? checkFormUpdate(lastBalance, consum)
                              : checkFormAdd(lastBalance, consum)}
                          </>
                        )
                      })()}
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </Table>
      </div>

      {/* Pending Entries Section */}
      {pendingEntries.length > 0 && (
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Pending Entries ({pendingEntries.length})</h5>
            <div>
              <Button
                color="danger"
                size="sm"
                outline
                className="me-2"
                onClick={clearPendingEntries}
                disabled={storeStatus?.loading}
              >
                <i className="bx bx-trash me-1"></i>
                Clear All
              </Button>
              <Button
                color="success"
                size="sm"
                onClick={handleSaveAll}
                disabled={storeStatus?.loading}
              >
                {storeStatus?.loading ? (
                  <>
                    <i className="bx bx-loader bx-spin me-1"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bx bx-save me-1"></i>
                    Save All ({pendingEntries.length})
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="table-responsive">
            <Table className="table table-bordered table-sm">
              <thead className="table-light">
                <tr>
                  <th style={{ whiteSpace: "nowrap" }}>ITEM</th>
                  <th style={{ whiteSpace: "nowrap" }}>PROVINCE</th>
                  <th style={{ whiteSpace: "nowrap" }}>FACILITY</th>
                  <th style={{ whiteSpace: "nowrap" }}>QUANTITY</th>
                  <th style={{ whiteSpace: "nowrap" }}>COMMENT</th>
                  <th style={{ whiteSpace: "nowrap" }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {pendingEntries.map((entry, index) => (
                  <tr key={entry.id}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {entry.item?.ip_item_description}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {entry.province?.province}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {entry.facility?.facility_name}
                    </td>
                    <td>{entry.quantity}</td>
                    <td>{entry.comment || "-"}</td>
                    <td>
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        onClick={() => removeFromPendingList(entry.id)}
                        disabled={storeStatus?.loading}
                      >
                        <i className="bx bx-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </>
  )
}

export default ProvinceConsumptionItems
